import { Suspense } from "react";
import { format, parseISO, differenceInDays } from "date-fns";
import { Row, Column } from "@tanstack/react-table";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { exportDataToCsv } from "@/utils/exportToCsv";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Button } from "@/components/ui/button";
import { Users, CalendarDays, Mail } from "lucide-react";

// We'll define a small date-sorting function for TanStack Table
function dateSort(rowA: any, rowB: any, columnId: string) {
  const dateA = new Date(rowA.getValue(columnId));
  const dateB = new Date(rowB.getValue(columnId));
  return dateA.getTime() - dateB.getTime();
}

// Define type for organization user
interface OrganizationUser {
  sub: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  userTypeId: string | null;
  userTypeName: string | null;
  createdAt: string;
}

interface OrganizationUsersTableProps {
  organizationUsers: OrganizationUser[];
  userTypeGroups: Record<string, OrganizationUser[]>;
}

function OrganizationUsersTableContent({
  organizationUsers,
  userTypeGroups,
}: OrganizationUsersTableProps) {
  // Define columns for the organization users table
  const userColumns = [
    {
      accessorKey: "user",
      header: ({ column }: { column: Column<OrganizationUser> }) => (
        <DataTableColumnHeader column={column} title="User" />
      ),
      enableSorting: true,
      meta: {
        className: "min-w-[200px]",
      },
      accessorFn: (row: OrganizationUser) =>
        `${row.firstName || ""} ${row.lastName || ""}`.trim(),
      cell: ({ row }: { row: Row<OrganizationUser> }) => {
        const user = row.original;
        const isNew =
          differenceInDays(new Date(), parseISO(user.createdAt)) < 60;

        return (
          <div className="flex items-center">
            <Avatar className="mr-2 h-6 w-6">
              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                {user.firstName?.[0]}
                {user.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="relative">
              <span className="text-xs font-medium">
                {user.firstName} {user.lastName}
              </span>
              {isNew && (
                <Badge
                  style={{
                    backgroundColor: "",
                    letterSpacing: "0.5px",
                    marginLeft: "12px",
                    zIndex: 10,
                  }}
                >
                  New!
                </Badge>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: ({ column }: { column: Column<OrganizationUser> }) => (
        <DataTableColumnHeader column={column} title="Email" />
      ),
      enableSorting: true,
      cell: ({ row }: { row: Row<OrganizationUser> }) => {
        const user = row.original;
        return (
          <div className="flex items-center space-x-1">
            <Mail className="text-muted-foreground h-3 w-3" />
            <span className="text-xs">{user.email}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "userTypeId",
      header: ({ column }: { column: Column<OrganizationUser> }) => (
        <DataTableColumnHeader column={column} title="User Type" />
      ),
      enableSorting: true,
      cell: ({ row }: { row: Row<OrganizationUser> }) => {
        const user = row.original;
        return (
          <span className="text-xs font-normal">
            {user.userTypeName ||
              (user.userTypeId
                ? user.userTypeId.substring(
                    user.userTypeId.lastIndexOf("-") + 1,
                  )
                : "N/A")}
          </span>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }: { column: Column<OrganizationUser> }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      enableSorting: true,
      sortingFn: dateSort,
      cell: ({ row }: { row: Row<OrganizationUser> }) => {
        const user = row.original;
        return (
          <div className="flex items-center space-x-1">
            <CalendarDays className="text-muted-foreground h-3 w-3" />
            <span className="text-xs">
              {format(parseISO(user.createdAt), "PP")}
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2 px-1">
        <Users className="text-primary h-4 w-4" />
        <h2 className="text-base font-semibold">Organization Users</h2>
      </div>
      <DataTable
        tableId="organization-detail-users"
        data={organizationUsers}
        columns={userColumns}
        searchColumn="email"
        searchPlaceholder="Search users..."
        facetedFilters={[
          {
            columnId: "userTypeId",
            title: "User Type",
            options: Object.entries(userTypeGroups).map(([typeId, users]) => ({
              label:
                users[0]?.userTypeName ||
                typeId.substring(typeId.lastIndexOf("-") + 1),
              value: typeId,
            })),
          },
        ]}
        defaultSort={[{ id: "createdAt", desc: true }]}
        floatingActionItems={(table) => {
          const selectedCount = table.getSelectedRowModel().rows.length;
          const selectedRows = table
            .getSelectedRowModel()
            .rows.map((r) => r.original);
          return [
            <div
              key="selectedCount"
              className="text-muted-foreground mr-2 flex items-center text-sm"
            >
              {selectedCount} selected
            </div>,
            <Button
              key="export-csv"
              variant="default"
              size="sm"
              className="space-x-1"
              onClick={() =>
                exportDataToCsv(selectedRows, "organization-users.csv")
              }
            >
              <span>Export CSV</span>
            </Button>,
          ];
        }}
      />
    </div>
  );
}

export function OrganizationUsersTable(props: OrganizationUsersTableProps) {
  return (
    <EcosuiteErrorBoundary>
      <Suspense
        fallback={<LoadingScreen text="Loading users..." className="h-96" />}
      >
        <OrganizationUsersTableContent {...props} />
      </Suspense>
    </EcosuiteErrorBoundary>
  );
}
